import React, { useState } from 'react';
import { Modal, Button, Typography, Space, Divider } from 'antd';
import dayjs from 'dayjs';
// Types
import { GetCbPendingExtensionRequests_getCbPendingExtensionRequests } from 'api/campaignV2/types/GetCbPendingExtensionRequests';
import { GetCampaignOffers_getCampaignOffers_entities } from 'api/campaignV2/types/GetCampaignOffers';
import { TaskExtensionRequestType } from 'api/graphql-global-types';
// Helpers
import { formatAppearanceDate } from 'helpers/deliverables';
// Hooks
import { useTaskExtensions } from 'hooks/useTaskExtensions';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import ExtensionHistory from '../ExtensionHistory/ExtensionHistory';
// Styles
import styles from './ReviewExtensionModal.module.scss';

const { Text } = Typography;

type ReviewExtensionModalProps = {
  isVisible: boolean;
  onClose: () => void;
  request: GetCbPendingExtensionRequests_getCbPendingExtensionRequests;
  offer: GetCampaignOffers_getCampaignOffers_entities;
  onSuccess: () => void;
  getCbTaskExtensionHistory: (options: any) => void;
  extensionHistoryData?: any[];
  extensionHistoryLoading?: boolean;
};

const getNeededForText = (
  request: GetCbPendingExtensionRequests_getCbPendingExtensionRequests
) => {
  const label =
    request.type === TaskExtensionRequestType.CONTENT
      ? 'Content'
      : request.type === TaskExtensionRequestType.EVENT
      ? 'Appearance'
      : 'Publish';

  return `${label} for deliverable ${
    (request.deliverable.task?.orderIdx ?? 0) + 1
  }`;
};

const getDeadlineText = (
  request: GetCbPendingExtensionRequests_getCbPendingExtensionRequests
) => {
  if (request.type === TaskExtensionRequestType.EVENT) {
    // Appearance date is stored on the deliverable's contentDueDate fields.
    return formatAppearanceDate(
      request.deliverable.contentDueDateUtc ??
        request.deliverable.contentDueDateTo
    );
  }

  return `${
    request.type === TaskExtensionRequestType.CONTENT
      ? dayjs(request.deliverable.contentDueDateUtc).format('MMMM D, YYYY')
      : dayjs(
          request.deliverable.publishDueDateUtc ??
            request.deliverable.publishDueDateTo
        ).format('MMMM D, YYYY')
  }`;
};

const ReviewExtensionModal: React.FC<ReviewExtensionModalProps> = ({
  isVisible,
  onClose,
  request,
  offer,
  onSuccess,
  getCbTaskExtensionHistory,
  extensionHistoryData,
  extensionHistoryLoading,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { approveTaskExtension, rejectTaskExtension } = useTaskExtensions();

  const isEventExtension = request.type === TaskExtensionRequestType.EVENT;

  const handleAction = async (action: 'approve' | 'reject') => {
    setIsProcessing(true);
    try {
      const actionFunction =
        action === 'approve' ? approveTaskExtension : rejectTaskExtension;

      await actionFunction({
        variables: {
          input: {
            extensionRequestId: request.id,
            offerId: offer.id,
          },
        },
      });

      successNotification(
        `Extension request ${
          action === 'approve' ? 'approved' : 'rejected'
        } successfully`
      );
      onClose();
      onSuccess();
    } catch (error: any) {
      errorNotification(
        error?.message ||
          'Something went wrong while processing the extension request. Please try again.'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      title="Review Extension Request"
      visible={isVisible}
      onCancel={onClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      <div className={styles.root}>
        <div className={styles.infoSection}>
          <div className={styles.infoRow}>
            <Text strong>Date of Request:</Text>
            <Text>{dayjs(request.createdAt).format('MMMM D, YYYY')}</Text>
          </div>

          <div className={styles.infoRow}>
            <Text strong>Requested Needed for:</Text>
            <Text>{getNeededForText(request)}</Text>
          </div>

          {request.requestedExtensionDays != null && (
            <div className={styles.infoRow}>
              <Text strong>Requested Extension:</Text>
              <Text>
                {request.requestedExtensionDays}{' '}
                {request.requestedExtensionDays === 1 ? 'day' : 'days'}
              </Text>
            </div>
          )}

          <div className={styles.infoRow}>
            <Text strong>Reason:</Text>
            <Text>{request.reason}</Text>
          </div>

          <div className={styles.warningSection}>
            <Text type="warning">
              {isEventExtension
                ? 'This will move the scheduled appearance date which is currently on '
                : 'This may impact posting deadline date which is on '}
              {getDeadlineText(request)}
            </Text>
          </div>
        </div>

        <Divider />

        <div className={styles.actions}>
          <Button onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Space>
            <Button
              danger
              onClick={() => handleAction('reject')}
              loading={isProcessing}
            >
              Reject
            </Button>
            <Button
              type="primary"
              onClick={() => handleAction('approve')}
              loading={isProcessing}
            >
              Approve
            </Button>
          </Space>
        </div>

        {/* Extension History */}
        <ExtensionHistory
          deliverableId={request.deliverable.id}
          offerId={offer.id}
          getCbTaskExtensionHistory={getCbTaskExtensionHistory}
          extensionHistoryData={extensionHistoryData}
          extensionHistoryLoading={extensionHistoryLoading}
        />
      </div>
    </Modal>
  );
};

export default ReviewExtensionModal;
